import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAPI } from "../../services/api";
import i18n from "../../i18n";
import axios from "axios";

// Helper functions for state persistence
const loadInitialState = () => {
  try {
    return {
      user: JSON.parse(localStorage.getItem("user")),
      token: localStorage.getItem("accessToken"),
      refreshToken: localStorage.getItem("refreshToken"),
      loading: false,
      error: null,
    };
  } catch (error) {
    console.error("Error loading auth state:", error);
    return {
      user: null,
      token: null,
      refreshToken: null,
      loading: false,
      error: null,
    };
  }
};

const storeTokens = (tokens) => {
  if (tokens.accessToken) {
    localStorage.setItem("accessToken", tokens.accessToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem("refreshToken", tokens.refreshToken);
  }
  if (tokens.user) {
    localStorage.setItem("user", JSON.stringify(tokens.user));
  }
};

const removeTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("passwordLastChanged");
};

// Async thunks
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (refreshToken, { rejectWithValue }) => {
    try {
      const response = await authAPI.refreshToken(refreshToken);
      storeTokens(response.data);
      return response.data;
    } catch (error) {
      removeTokens();
      return rejectWithValue(
        error.response?.data?.message || i18n.t("errors.tokenRefreshFailed")
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async ({ formData, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (!response.data.success) {
        return rejectWithValue(response.data.message || "Upload failed");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUserPreferences = createAsyncThunk(
  "auth/updateUserPreferences",
  async ({ language }, { rejectWithValue, dispatch, getState }) => {
    try {
      // Get current auth state
      const { auth } = getState();

      // Always update local state
      i18n.changeLanguage(language);
      localStorage.setItem("i18nextLng", language);

      // Only make API call if authenticated
      if (auth.token) {
        const response = await axios.put(
          "/api/user/preferences",
          { language },
          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
        );

        if (response.data.success) {
          // Update Redux state with the new user data
          dispatch(updateUser(response.data.user));
        }

        return response.data;
      }

      // If not authenticated, just update the user object in redux if it exists
      if (auth.user) {
        dispatch(updateUser({ language }));
      }

      return { success: true };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update preferences"
      );
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      if (response.data?.user) {
        storeTokens(response.data);
        return response.data;
      }
      return rejectWithValue(i18n.t("errors.loginFailed"));
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || i18n.t("errors.loginFailed")
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: loadInitialState(),
  reducers: {
    logout: (state) => {
      // Clear all state
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.error = null;

      // Clear local storage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("passwordLastChanged");
      localStorage.removeItem("currentProfile");

      // Additional cleanup as needed
      localStorage.removeItem("i18nextLng"); // Optional - may want to keep language preference

      // Reset API defaults
      // window.location.href = "/login"; // Force a full page reload to clear any app state
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      // Update user state with new data
      state.user = {
        ...state.user,
        ...action.payload,
      };

      // If language is being updated, ensure i18n is synced
      if (action.payload.language) {
        i18n.changeLanguage(action.payload.language);
        localStorage.setItem("i18nextLng", action.payload.language);
      }

      // Update localStorage
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUser = { ...storedUser, ...action.payload };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
        if (action.payload.user.passwordLastChanged) {
          localStorage.setItem(
            "passwordLastChanged",
            action.payload.user.passwordLastChanged
          );
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        removeTokens();
      })
      // Token refresh cases
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        if (action.payload.user) {
          state.user = { ...state.user, ...action.payload.user };
        }
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        removeTokens();
      })
      .addCase("auth/sessionExpired", (state, action) => {
        state.error = action.payload;
      })
      // Update user profile cases
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.user) {
          state.user = { ...state.user, ...action.payload.user };
          localStorage.setItem("user", JSON.stringify(state.user));
        }
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Global error matcher
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action) => {
          if (action.error?.message?.includes("Password has been changed")) {
            state.user = null;
            state.token = null;
            state.refreshToken = null;
            state.error = i18n.t("auth.login.passwordChanged");
            removeTokens();
          }
        }
      );
  },
});

// Selectors
export const selectIsAuthenticated = (state) => !!state.auth.token;
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

export const { logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
